import React, { useEffect, useCallback, useState, useRef } from 'react';
import { collection, getDocs, addDoc, serverTimestamp, query, orderBy, limit, where } from 'firebase/firestore';
import { db } from '../firebase';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAppData } from '../context/AppDataContext';
import '../styles/ClientList.css';

export default function AddOrderPage() {
  const { clients, refresh } = useAppData();
  const navigate = useNavigate();
  const location = useLocation();
  const copiedOrder = location.state?.copiedOrder || null;

  // Client selected in the dropdown, awaiting confirmation
  const [pendingClientId, setPendingClientId] = useState('');
  const [creating, setCreating] = useState(false);

  // Guards the copied-order auto-create so it can only fire once per page load
  const copyStartedRef = useRef(false);

  // Sort alphabetically for the dropdown
  const sortedClients = [...clients].sort((a, b) => (a.name || '').localeCompare(b.name || ''));

  // Helper function to send system message about copying
  const sendCopyNotification = async (newOrderId, sourceOrderJobNumber, clientId) => {
    try {
      const message = {
        sender: "system",
        senderName: "System",
        senderRole: "system",
        recipientType: "internal",
        text: `Order copied from Job #${sourceOrderJobNumber}`,
        createdAt: serverTimestamp(),
        read: false,
        clientId,
        orderId: newOrderId,
        isOrderMessage: true,
      };

      await addDoc(collection(db, "clients", clientId, "messages"), message);
    } catch (error) {
      console.error('Error sending copy notification:', error);
    }
  };

  const createOrderForClient = useCallback(async (clientId) => {
    if (!clientId) return;

    const client = clients.find(c => c.id === clientId);
    if (!client) return;

    setCreating(true);

    const {
      name: dealershipName = '',
      phone: dealershipPhone = '',
      phone2: dealershipPhone2 = '',
      generalManager = '',
      dealershipWebsite = '',
      voucherTemplate = '',
      manufacturer = '',
      street1: dealershipStreet1 = '',
      street2: dealershipStreet2 = '',
      state: dealershipState = '',
      email: dealershipEmail = '',
      email2: dealershipEmail2 = '',
      notes: dealershipNotes = '',
      dealershipHours: dealershipHoursObj = {},
      hours: hoursObj = {},
    } = client;

    // Job number generation: jobNumber is a string, so sorting by it puts
    // "SRMG-999" above "SRMG-1000". Instead, pull the most recently created
    // orders and take the highest SRMG number numerically.
    // The createdAt > epoch filter keeps only real Timestamps (skips any
    // legacy string/missing createdAt values) and needs no composite index.
    const ordersRef = collection(db, 'orders');
    const recentQuery = query(
      ordersRef,
      where('createdAt', '>', new Date(0)),
      orderBy('createdAt', 'desc'),
      limit(50)
    );
    const recentSnap = await getDocs(recentQuery);
    let maxSeq = 0;
    recentSnap.forEach(d => {
      const match = /^SRMG-(\d+)$/.exec(d.data().jobNumber || '');
      if (match) maxSeq = Math.max(maxSeq, parseInt(match[1], 10));
    });

    // Safety net: never silently restart numbering at SRMG-001
    if (maxSeq === 0) {
      alert('Could not determine the next job number. No order was created - please try again.');
      setCreating(false);
      // Copy flow has no dropdown to retry from - send the user back to Orders
      if (copiedOrder) navigate('/orders', { replace: true });
      return;
    }

    const jobNumber = `SRMG-${String(maxSeq + 1).padStart(3, '0')}`;

    // FIXED: Build newOrder with only selected fields from the modal
    const newOrder = {
      // Always include these base fields
      clientId,
      jobNumber,
      createdAt: serverTimestamp(),
      status: "Incomplete - Need Art",
      
      // Dealership info (always included)
      dealershipName,
      dealershipPhone,
      dealershipPhone2,
      generalManager,
      dealershipWebsite,
      voucherTemplate,
      manufacturer,
      dealershipStreet1,
      dealershipStreet2,
      dealershipState,
      dealershipEmail,
      dealershipEmail2,
      dealershipNotes,
      dealershipHours: dealershipHoursObj,
      hours: hoursObj,

      // Always reset these fields for new orders
      adminApproval: "Pending",
      clientApproval: "Pending",
      artUploaded: false,
      insuranceLinked: false,
      prizeboardUploaded: false,
      insuranceFile: null,
      insurancePath: null,
      prizeboardFile: null,

      // Default structures
      drops: [{ startDate: '', endDate: '', dduDate: '', quantity: '' }],
      vehicles: [],
      prizes: Array(5).fill().map(() => ({ prize: '', custom: '', odds: '', value: '' })),
      totalOdds: 50000
    };

    // FIXED: Only add the specific fields that were selected in the modal
    if (copiedOrder) {
      // The modal should have already filtered what gets included in copiedOrder
      // But let's be extra careful and only copy safe fields

      // Order details (if selected)
      if (copiedOrder.campaignName !== undefined) newOrder.campaignName = copiedOrder.campaignName;
      if (copiedOrder.campaignNameOther !== undefined) newOrder.campaignNameOther = copiedOrder.campaignNameOther;
      if (copiedOrder.size !== undefined) newOrder.size = copiedOrder.size;
      if (copiedOrder.attachment !== undefined) newOrder.attachment = copiedOrder.attachment;
      if (copiedOrder.printer !== undefined) newOrder.printer = copiedOrder.printer;
      if (copiedOrder.printerOther !== undefined) newOrder.printerOther = copiedOrder.printerOther;
      if (copiedOrder.quantity !== undefined) newOrder.quantity = copiedOrder.quantity;
      if (copiedOrder.sort !== undefined) newOrder.sort = copiedOrder.sort;
      if (copiedOrder.sortOther !== undefined) newOrder.sortOther = copiedOrder.sortOther;
      if (copiedOrder.notes !== undefined) newOrder.notes = copiedOrder.notes;
      if (copiedOrder.paperType !== undefined) newOrder.paperType = copiedOrder.paperType;
      if (copiedOrder.paperTypeOther !== undefined) newOrder.paperTypeOther = copiedOrder.paperTypeOther;
      if (copiedOrder.staffed !== undefined) newOrder.staffed = copiedOrder.staffed;

      // Drop dates (if selected)
      if (copiedOrder.drops) {
        newOrder.drops = copiedOrder.drops.map(drop => ({
          startDate: '',
          endDate: '',
          dduDate: '',
          quantity: drop.quantity || ''
        }));
      }

      // Vehicles (if selected)
      if (copiedOrder.vehicles) {
        newOrder.vehicles = copiedOrder.vehicles;
      }

      // Prizes (if selected)
      if (copiedOrder.prizes) {
        newOrder.prizes = copiedOrder.prizes;
      }
      if (copiedOrder.totalOdds) {
        newOrder.totalOdds = copiedOrder.totalOdds;
      }

      // Insurance policy (if selected)
      if (copiedOrder.insuranceFile) {
        newOrder.insuranceFile = copiedOrder.insuranceFile;
        newOrder.insurancePath = copiedOrder.insurancePath;
        newOrder.insuranceLinked = true;
      }

      // Prizeboard (if selected)
      if (copiedOrder.prizeboardFile) {
        newOrder.prizeboardFile = copiedOrder.prizeboardFile;
        newOrder.prizeboardUploaded = true;
      }

      // Design files would be handled differently since they're in a subcollection
    }

    const docRef = await addDoc(ordersRef, newOrder);

    // Refresh the orders cache so the new order appears everywhere immediately
    refresh(['orders']);
    
    // FIXED: Send auto-message about copying
    if (copiedOrder && copiedOrder.originalJobNumber) {
      await sendCopyNotification(docRef.id, copiedOrder.originalJobNumber, clientId);
    }

    // replace: drops /orders/new (and its copiedOrder state) from history,
    // so the Back button can't re-trigger the copy
    navigate(`/orders/${docRef.id}`, { replace: true });
  }, [clients, copiedOrder, navigate, refresh]);

  // Copied orders skip confirmation - the client is inherited from the source order
  useEffect(() => {
    if (copiedOrder && copiedOrder.clientId && clients.length > 0 && !copyStartedRef.current) {
      copyStartedRef.current = true;
      createOrderForClient(copiedOrder.clientId);
    }
  }, [copiedOrder, clients, createOrderForClient]);

  const pendingClient = clients.find(c => c.id === pendingClientId) || null;

  return (
    <div className="client-page">
      <div className="client-header">
        <h1 className="gradient-text">{copiedOrder ? 'Copying Order...' : 'Create New Order'}</h1>
      </div>

      {/* Only show dropdown if we don't have a pre-selected client */}
      {!(copiedOrder && copiedOrder.clientId) && (
        <div className="client-actions">
          <select
            onChange={(e) => setPendingClientId(e.target.value)}
            value={pendingClientId}
            className="client-actions"
            disabled={creating}
          >
            <option value="" disabled>Select Dealership</option>
            {sortedClients.map(c => (
              <option key={c.id} value={c.id}>{c.name}</option>
            ))}
          </select>

          {pendingClient && !creating && (
            <div className="client-actions" style={{ marginTop: '16px', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '12px' }}>
              <p style={{ margin: 0 }}>
                Create a new order for <strong>{pendingClient.name}</strong>
                {pendingClient.state ? ` (${pendingClient.state})` : ''}?
              </p>
              <div style={{ display: 'flex', gap: '12px' }}>
                <button
                  className="client-actions"
                  onClick={() => createOrderForClient(pendingClientId)}
                >
                  Yes, Create Order
                </button>
                <button
                  className="client-actions"
                  onClick={() => setPendingClientId('')}
                >
                  Cancel
                </button>
              </div>
            </div>
          )}

          {creating && (
            <div className="client-actions" style={{ marginTop: '16px' }}>
              <p>Creating order for {pendingClient?.name || 'selected dealership'}...</p>
            </div>
          )}
        </div>
      )}

      {/* Show loading message when auto-proceeding */}
      {copiedOrder && copiedOrder.clientId && (
        <div className="client-actions">
          <p>Creating order for selected dealership...</p>
        </div>
      )}
    </div>
  );
}